import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  emoji?: string;
}

const FAQItem = ({ question, answer, emoji }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      className="border border-[#41392e] rounded-lg bg-[#9AA581]/10"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <button
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#9AA581]/20 transition-colors duration-300"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-4">
          {emoji && <span className="text-2xl">{emoji}</span>}
          <span className="text-lg font-serif text-white">{question}</span>
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-[#c9ab81] transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      
      <div 
        className={`grid transition-all duration-500 ease-in-out ${
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-6 py-4 border-t border-[#41392e] text-white/80 leading-relaxed">
            {answer}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FAQSection = ({ title, items }: { title: string; items: FAQItemProps[] }) => (
  <div className="space-y-4">
    <motion.h2 
      className="text-3xl font-serif mb-8 text-white"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      {title}
    </motion.h2>
    {items.map((item, index) => (
      <FAQItem key={index} {...item} />
    ))}
  </div>
);

const FAQ = () => {
  const { t } = useTranslation();

  const sections = [
    {
      title: t('faq.sections.participation.title'),
      items: [
        {
          question: t('faq.sections.participation.questions.who.question'),
          answer: t('faq.sections.participation.questions.who.answer'),
          emoji: "🧘‍♀️"
        },
        {
          question: t('faq.sections.participation.questions.experience.question'),
          answer: t('faq.sections.participation.questions.experience.answer')
        },
        {
          question: t('faq.sections.participation.questions.alone.question'),
          answer: t('faq.sections.participation.questions.alone.answer')
        },
        {
          question: t('faq.sections.participation.questions.accompanied.question'),
          answer: t('faq.sections.participation.questions.accompanied.answer')
        },
        {
          question: t('faq.sections.participation.questions.pregnant.question'),
          answer: t('faq.sections.participation.questions.pregnant.answer')
        }
      ]
    },
    {
      title: t('faq.sections.accommodation.title'),
      items: [
        {
          question: t('faq.sections.accommodation.questions.lodging.question'),
          answer: t('faq.sections.accommodation.questions.lodging.answer'),
          emoji: "🏡"
        },
        {
          question: t('faq.sections.accommodation.questions.linens.question'),
          answer: t('faq.sections.accommodation.questions.linens.answer')
        },
        {
          question: t('faq.sections.accommodation.questions.wifi.question'),
          answer: t('faq.sections.accommodation.questions.wifi.answer')
        },
        {
          question: t('faq.sections.accommodation.questions.transport.question'),
          answer: t('faq.sections.accommodation.questions.transport.answer')
        },
        {
          question: t('faq.sections.accommodation.questions.bring.question'),
          answer: t('faq.sections.accommodation.questions.bring.answer')
        }
      ]
    },
    {
      title: t('faq.sections.food.title'),
      items: [
        {
          question: t('faq.sections.food.questions.meals.question'),
          answer: t('faq.sections.food.questions.meals.answer'),
          emoji: "🥗"
        },
        {
          question: t('faq.sections.food.questions.allergies.question'),
          answer: t('faq.sections.food.questions.allergies.answer')
        },
        {
          question: t('faq.sections.food.questions.drinks.question'),
          answer: t('faq.sections.food.questions.drinks.answer')
        },
        {
          question: t('faq.sections.food.questions.alcohol.question'),
          answer: t('faq.sections.food.questions.alcohol.answer')
        }
      ]
    },
    {
      title: t('faq.sections.activities.title'),
      items: [
        {
          question: t('faq.sections.activities.questions.mandatory.question'),
          answer: t('faq.sections.activities.questions.mandatory.answer'),
          emoji: "📅"
        },
        {
          question: t('faq.sections.activities.questions.workshops.question'),
          answer: (() => {
            const workshops = t('faq.sections.activities.questions.workshops.answer', { returnObjects: true });
            return (
              <ul className="space-y-2">
                {Array.isArray(workshops) ? 
                  workshops.map((item: string, index: number) => (
                    <li key={index}>✨ {item}</li>
                  )) : 
                  <li>✨ {workshops}</li>
                }
              </ul>
            );
          })()
        },
        {
          question: t('faq.sections.activities.questions.athletic.question'),
          answer: t('faq.sections.activities.questions.athletic.answer')
        },
        {
          question: t('faq.sections.activities.questions.free.question'),
          answer: t('faq.sections.activities.questions.free.answer')
        },
        {
          question: t('faq.sections.activities.questions.after.question'),
          answer: t('faq.sections.activities.questions.after.answer')
        }
      ]
    },
    {
      title: t('faq.sections.booking.title'),
      items: [
        {
          question: t('faq.sections.booking.questions.reserve.question'),
          answer: t('faq.sections.booking.questions.reserve.answer'),
          emoji: "📩"
        },
        {
          question: t('faq.sections.booking.questions.limited.question'),
          answer: t('faq.sections.booking.questions.limited.answer')
        },
        {
          question: t('faq.sections.booking.questions.payment.question'),
          answer: t('faq.sections.booking.questions.payment.answer'),
          emoji: "💳"
        },
        {
          question: t('faq.sections.booking.questions.cancel.question'),
          answer: t('faq.sections.booking.questions.cancel.answer')
        },
        {
          question: t('faq.sections.booking.questions.gift.question'),
          answer: t('faq.sections.booking.questions.gift.answer')
        }
      ]
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113190/1736183405038_2_rfj0d1.jpg')"
          }}
        >
          <div className="absolute inset-0 bg-[#9AA581]/60" />
        </motion.div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-serif mb-6 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('faq.title')}
            <br />
            <span className="text-[#c9ab81] italic">{t('faq.subtitle')}</span>
          </motion.h1>
          <motion.p 
            className="text-xl font-light text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('faq.description')}
          </motion.p>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="space-y-16">
          {sections.map((section, index) => (
            <FAQSection key={index} title={section.title} items={section.items} />
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div 
          className="mt-20 text-center space-y-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xl font-light text-white">
            {t('faq.contact.text')}
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          >
            <a 
              href="https://wa.me/23058241702"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-12 py-4 border border-[#c9ab81] text-[#c9ab81] hover:bg-[#c9ab81] hover:text-black transition-all duration-300"
            >
              {t('faq.contact.button')}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;